import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import {
  type AmendmentRequest,
  ApiError,
  type FilingData,
  type FilingTransaction,
  type ValidationResult,
  filingApi,
} from '../../services/api';
import { fmtUSD as fmt, fmtDate, fmtPct } from '../../utils/fmt';
import { type MetricKey, TransactionPanel } from './filing/TransactionPanel';

function BackButton({ onClick }: { onClick: () => void }): JSX.Element {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        color: hov ? '#0F172A' : '#64748B',
        padding: '4px 0',
        marginBottom: 16,
        transition: 'color 0.1s',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M9 11L5 7l4-4"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      All returns
    </button>
  );
}

function ApprovedBanner({
  status,
  approvedAt,
  approvedBy,
}: { status?: string; approvedAt?: string; approvedBy?: string }): JSX.Element | null {
  if (status === 'pending_review' || !status) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        background: '#F0FDF4',
        border: '1px solid #BBF7D0',
        borderRadius: 10,
        padding: '10px 16px',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" fill="#16A34A" opacity=".15" />
        <path
          d="M5 8l2.5 2.5L11 5.5"
          stroke="#16A34A"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#15803D' }}>
        {status === 'filed' ? 'Filed' : 'Approved'}
        {approvedBy && ` · ${approvedBy}`}
        {approvedAt && ` · ${fmtDate(approvedAt)}`}
      </span>
    </div>
  );
}

const METRICS = [
  {
    key: 'gross_sales' as MetricKey,
    label: 'Gross Sales',
    sub: 'All transactions in period',
    accent: '#64748B',
  },
  {
    key: 'exempt_sales' as MetricKey,
    label: 'Exempt Sales',
    sub: 'Exempt by product or rule',
    accent: '#7C3AED',
  },
  {
    key: 'taxable_sales' as MetricKey,
    label: 'Taxable Sales',
    sub: 'Gross minus exempt',
    accent: '#0284C7',
  },
  {
    key: 'tax_collected' as MetricKey,
    label: 'Tax Collected',
    sub: 'Collected from buyers',
    accent: '#0F172A',
  },
  {
    key: 'tax_due' as MetricKey,
    label: 'Tax Due',
    sub: 'Amount to remit to state',
    accent: '#1B4F8C',
  },
];

interface SummarySectionProps {
  data: FilingData | null;
  loading: boolean;
  activeFilter: MetricKey | null;
  onFilter: (key: MetricKey | null) => void;
}
function SummarySection({
  data,
  loading,
  activeFilter,
  onFilter,
}: SummarySectionProps): JSX.Element {
  const mobile = useWindowWidth() < 640;
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#94A3B8',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          marginBottom: 10,
        }}
      >
        Return Summary — click any figure to see the underlying transactions
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: mobile ? 'wrap' : 'nowrap' }}>
        {METRICS.map((m) => {
          const active = activeFilter === m.key;
          return (
            <div
              key={m.key}
              onClick={() => !loading && onFilter(active ? null : m.key)}
              style={{
                flex: mobile ? '0 0 calc(50% - 5px)' : 1,
                background: active ? '#EFF6FF' : '#fff',
                border: `1px solid ${active ? '#BFDBFE' : '#E2E8F0'}`,
                borderTop: `3px solid ${loading ? '#E2E8F0' : m.accent}`,
                borderRadius: 10,
                padding: '14px 16px',
                cursor: loading ? 'default' : 'pointer',
                transition: 'box-shadow 0.12s, border-color 0.12s',
                position: 'relative',
                boxSizing: 'border-box',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.07)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {loading ? (
                <>
                  <div
                    className="skeleton-box"
                    style={{ width: 80, height: 10, marginBottom: 10 }}
                  />
                  <div
                    className="skeleton-box"
                    style={{ width: 100, height: 22, marginBottom: 6 }}
                  />
                  <div className="skeleton-box" style={{ width: 70, height: 10 }} />
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#94A3B8',
                      textTransform: 'uppercase',
                      letterSpacing: '.06em',
                      marginBottom: 6,
                    }}
                  >
                    {m.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', marginBottom: 2 }}>
                    {fmt(data?.[m.key])}
                  </div>
                  <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 16 }}>{m.sub}</div>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 10,
                      right: 12,
                      fontSize: 10,
                      color: active ? '#2563EB' : '#94A3B8',
                      fontWeight: active ? 700 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    {active ? 'Hide transactions' : 'View transactions'}
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2.5 5h5M5 2.5l2.5 2.5L5 7.5"
                        stroke="currentColor"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const JLEVEL: Record<string, string> = {
  state: 'State',
  county: 'County',
  city: 'City',
  special_district: 'Special District',
};
const J = { name: '0 0 200px', level: '0 0 130px', taxable: '0 0 150px', rate: '0 0 90px', tax: 1 };
const TH: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
};

function JurisdictionTable({
  jurisdictions,
  loading,
}: { jurisdictions?: FilingData['jurisdictions']; loading: boolean }): JSX.Element | null {
  if (loading)
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #E2E8F0',
          borderRadius: 10,
          padding: '14px 16px',
          marginBottom: 20,
        }}
      >
        <div className="skeleton-box" style={{ width: '100%', height: 80, borderRadius: 6 }} />
      </div>
    );
  if (!jurisdictions?.length) return null;
  const total = jurisdictions.reduce((s, r) => s + (r.tax_amount ?? 0), 0);
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#94A3B8',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          marginBottom: 10,
        }}
      >
        Jurisdiction Breakdown
      </div>
      <div
        style={{
          background: '#fff',
          border: '1px solid #E2E8F0',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            background: '#F8FAFC',
            padding: '8px 16px',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <span style={{ flex: J.name, ...TH }}>Jurisdiction</span>
          <span style={{ flex: J.level, ...TH }}>Level</span>
          <span style={{ flex: J.taxable, textAlign: 'right', ...TH }}>Taxable Sales</span>
          <span style={{ flex: J.rate, textAlign: 'right', ...TH }}>Rate</span>
          <span style={{ flex: J.tax, textAlign: 'right', ...TH }}>Tax</span>
        </div>
        {jurisdictions.map((j, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              padding: '10px 16px',
              fontSize: 13,
              borderBottom: '1px solid #F1F5F9',
            }}
          >
            <span style={{ flex: J.name, fontWeight: 500, color: '#0F172A' }}>
              {j.jurisdiction_name}
            </span>
            <span style={{ flex: J.level, color: '#64748B', fontSize: 12 }}>
              {JLEVEL[j.jurisdiction_level] ?? j.jurisdiction_level}
            </span>
            <span style={{ flex: J.taxable, textAlign: 'right', color: '#475569' }}>
              {fmt(j.taxable_amount)}
            </span>
            <span style={{ flex: J.rate, textAlign: 'right', color: '#475569', fontSize: 12 }}>
              {fmtPct(j.rate)}
            </span>
            <span style={{ flex: J.tax, textAlign: 'right', fontWeight: 700, color: '#0F172A' }}>
              {fmt(j.tax_amount)}
            </span>
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            padding: '10px 16px',
            fontSize: 13,
            borderTop: '2px solid #E2E8F0',
            background: '#F8FAFC',
          }}
        >
          <span style={{ flex: J.name, fontWeight: 700, color: '#0F172A' }}>Total</span>
          <span style={{ flex: J.level }} />
          <span style={{ flex: J.taxable }} />
          <span style={{ flex: J.rate }} />
          <span style={{ flex: J.tax, textAlign: 'right', fontWeight: 800, color: '#0F172A' }}>
            {fmt(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Flag / Amendment modal ────────────────────────────────────────────────────

interface FlagModalProps {
  tx: FilingTransaction;
  organizationId: string;
  onClose: () => void;
  onFlagged: (txId: string) => void;
}
function FlagModal({ tx, organizationId, onClose, onFlagged }: FlagModalProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [ticCode, setTicCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  function handleSubmit(): void {
    if (!reason.trim()) {
      setError('Please describe the issue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const req: AmendmentRequest = {
      reason: reason.trim(),
      corrected_tic_code: ticCode.trim() || null,
    };
    filingApi
      .flagTransaction(organizationId, tx.transaction_id, req)
      .then(() => {
        onFlagged(tx.transaction_id);
        onClose();
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setSubmitting(false));
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.55)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: 480,
          maxWidth: 'calc(100vw - 40px)',
          boxShadow: '0 20px 60px rgba(0,0,0,.18)',
        }}
      >
        <div style={{ padding: '20px 22px 14px', borderBottom: '1px solid #F1F5F9' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 2 }}>
            Flag transaction for amendment
          </div>
          <div style={{ fontSize: 12, color: '#64748B' }}>
            {tx.order_id} · {tx.product_name}
          </div>
        </div>
        <div style={{ padding: '16px 22px' }}>
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#64748B',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                marginBottom: 6,
              }}
            >
              Issue description (required)
            </div>
            <textarea
              ref={textareaRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe what is incorrect (e.g. wrong TIC code, incorrect taxable amount, duplicate transaction)"
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                fontSize: 13,
                color: '#0F172A',
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ marginBottom: 6 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#64748B',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                marginBottom: 6,
              }}
            >
              Corrected TIC code (optional)
            </div>
            <input
              type="text"
              value={ticCode}
              onChange={(e) => setTicCode(e.target.value)}
              placeholder="e.g. 1001"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                fontSize: 13,
                color: '#0F172A',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
          {error && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: '#BE123C',
                background: '#FFF1F2',
                border: '1px solid #FECDD3',
                borderRadius: 6,
                padding: '8px 12px',
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{ display: 'flex', gap: 10, padding: '0 22px 20px', justifyContent: 'flex-end' }}
        >
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 600,
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              background: '#fff',
              color: '#475569',
              cursor: submitting ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '8px 20px',
              fontSize: 13,
              fontWeight: 700,
              border: 'none',
              borderRadius: 8,
              background: submitting ? '#CBD5E1' : '#DC2626',
              color: '#fff',
              cursor: submitting ? 'default' : 'pointer',
            }}
          >
            {submitting ? 'Flagging…' : 'Flag for amendment'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ApproveModalProps {
  data: FilingData | null;
  stateName: string;
  period: string;
  onConfirm: () => void;
  onCancel: () => void;
  approving: boolean;
  approveError: string | null;
}
function ApproveModal({
  data,
  stateName,
  period,
  onConfirm,
  onCancel,
  approving,
  approveError,
}: ApproveModalProps): JSX.Element {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.55)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: 520,
          maxWidth: 'calc(100vw - 40px)',
          boxShadow: '0 20px 60px rgba(0,0,0,.18)',
        }}
      >
        <div style={{ padding: '22px 24px 16px', borderBottom: '1px solid #F1F5F9' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: '#FFF7ED',
                border: '1px solid #FED7AA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v5M8 10v.5" stroke="#C2410C" strokeWidth="2" strokeLinecap="round" />
                <circle cx="8" cy="8" r="6.5" stroke="#C2410C" strokeWidth="1.5" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 3 }}>
                Approve {stateName} {period} return?
              </div>
              <div style={{ fontSize: 12, color: '#64748B' }}>
                This action is permanent and cannot be undone.
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '18px 24px' }}>
          <div
            style={{
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 16,
            }}
          >
            {(
              [
                ['Gross sales', fmt(data?.gross_sales)],
                ['Exempt sales', fmt(data?.exempt_sales)],
                ['Taxable sales', fmt(data?.taxable_sales)],
                ['Tax collected from buyers', fmt(data?.tax_collected)],
                [`Tax due to ${stateName}`, fmt(data?.tax_due)],
              ] as [string, string][]
            ).map(([label, value], i, arr) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  padding: '5px 0',
                  borderBottom: i < arr.length - 1 ? '1px solid #F1F5F9' : 'none',
                }}
              >
                <span style={{ color: '#475569' }}>{label}</span>
                <span style={{ fontWeight: 700, color: '#0F172A' }}>{value}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.65, margin: '0 0 10px' }}>
            By approving, you confirm these figures are complete and accurate for the {period}{' '}
            period. STAC will lock this return and generate the final XML for download.
          </p>
          <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.65, margin: '0 0 10px' }}>
            <strong style={{ color: '#0F172A' }}>If a correction is needed after approval,</strong>{' '}
            flag the affected transaction below — STAC creates a new amendment return linked to this
            one. The original stays on file; STAC cannot reverse or edit an approved return in
            place.
          </p>
          <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>
            This approval will be logged with your name and the current timestamp.
          </p>
          {approveError && (
            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                color: '#BE123C',
                background: '#FFF1F2',
                border: '1px solid #FECDD3',
                borderRadius: 6,
                padding: '8px 12px',
              }}
            >
              {approveError}
            </div>
          )}
        </div>
        <div
          style={{ display: 'flex', gap: 10, padding: '0 24px 22px', justifyContent: 'flex-end' }}
        >
          <button
            onClick={onCancel}
            disabled={approving}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 600,
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              background: '#fff',
              color: '#475569',
              cursor: approving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={approving}
            style={{
              padding: '8px 22px',
              fontSize: 13,
              fontWeight: 700,
              border: 'none',
              borderRadius: 8,
              background: approving ? '#CBD5E1' : '#0F172A',
              color: '#fff',
              cursor: approving ? 'default' : 'pointer',
              minWidth: 170,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {approving ? (
              'Approving…'
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M2 7L5.5 10.5 11 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Approve &amp; lock return
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const CHECK_LABELS: Record<string, string> = {
  double_entry: 'Double-entry — engine-computed tax matches tax collected',
  ser_prerequisites: 'SST prerequisites — FEIN and state registration on file',
  ser_schema: 'SER XML builds and passes SST schema (XSD) validation',
};

function ValidationPanel({
  result,
  onDismiss,
}: { result: ValidationResult; onDismiss: () => void }): JSX.Element {
  const ok = result.valid;
  return (
    <div
      style={{
        background: ok ? '#F0FDF4' : '#FFF1F2',
        border: `1px solid ${ok ? '#BBF7D0' : '#FECDD3'}`,
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: ok ? '#15803D' : '#BE123C' }}>
          {ok ? 'Pre-filing validation passed' : 'Pre-filing validation failed'}
          {!result.ser_applicable && ' · non-SST state, schema check not applicable'}
        </span>
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            color: '#64748B',
            padding: 0,
          }}
        >
          Dismiss
        </button>
      </div>
      {result.checks.map((c) => (
        <div
          key={c.check}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0' }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            style={{ flexShrink: 0, marginTop: 1 }}
          >
            {c.passed ? (
              <path
                d="M2.5 7.5L5.5 10.5 11.5 4"
                stroke="#16A34A"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="M3.5 3.5l7 7M10.5 3.5l-7 7"
                stroke="#DC2626"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            )}
          </svg>
          <div>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>
              {CHECK_LABELS[c.check] ?? c.check}
            </span>
            {c.error && (
              <div style={{ fontSize: 12, color: '#9F1239', marginTop: 2 }}>{c.error}</div>
            )}
            {Array.isArray(c.details) &&
              c.details.map((d, i) => (
                <div
                  key={i}
                  style={{ fontSize: 11, color: '#9F1239', marginTop: 2, fontFamily: 'monospace' }}
                >
                  {String(d)}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DownloadButtons({
  organizationId,
  filingId,
}: { organizationId: string; filingId: string }): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleDownload(format: string): void {
    setDownloading(true);
    setError(null);
    filingApi
      .downloadFiling(organizationId, filingId, format)
      .catch((e: Error) => setError(e.message))
      .finally(() => setDownloading(false));
  }

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '9px 20px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    border: 'none',
    cursor: downloading ? 'default' : 'pointer',
  };
  const icon = (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M6.5 2v6.5M4 6.5l2.5 2.5 2.5-2.5M2 10v1a.5.5 0 00.5.5h8a.5.5 0 00.5-.5v-1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={() => handleDownload('xml')}
          disabled={downloading}
          style={{ ...base, background: downloading ? '#94A3B8' : '#1B4F8C', color: '#fff' }}
        >
          {icon} {downloading ? 'Downloading…' : 'Download XML'}
        </button>
        <button
          type="button"
          onClick={() => handleDownload('csv')}
          disabled={downloading}
          style={{ ...base, background: '#fff', color: '#0F172A', border: '1px solid #E2E8F0' }}
        >
          {icon} Download CSV
        </button>
      </div>
      {error && <div style={{ marginTop: 8, fontSize: 12, color: '#BE123C' }}>{error}</div>}
    </div>
  );
}

interface FilingReviewProps {
  stateCode: string;
  stateName: string;
  period: string;
  onBack: () => void;
}

export default function FilingReview({
  stateCode,
  stateName: stateNameProp,
  period,
  onBack,
}: FilingReviewProps): JSX.Element {
  const { organizationId } = useAuth();
  const mobile = useWindowWidth() < 640;
  const [data, setData] = useState<FilingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [txFilter, setTxFilter] = useState<MetricKey | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [flaggedIds, setFlaggedIds] = useState<Set<string>>(new Set());
  const [flagTarget, setFlagTarget] = useState<FilingTransaction | null>(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);

  const stateName = data?.state_name ?? stateNameProp ?? stateCode;
  const isApproved = data?.status === 'approved' || data?.status === 'filed';

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    filingApi
      .getReview(organizationId, stateCode, period)
      .then(setData)
      .catch((e: Error) => {
        setError(e.message);
        setErrorStatus(e instanceof ApiError ? e.status : null);
      })
      .finally(() => setLoading(false));
  }, [organizationId, stateCode, period]);

  useEffect(load, [load]);

  function handleValidate(): void {
    if (!data?.filing_id) return;
    setValidating(true);
    filingApi
      .validate(organizationId, data.filing_id)
      .then(setValidation)
      .catch((e: Error) =>
        setValidation({
          valid: false,
          ser_applicable: false,
          checks: [{ check: 'request', passed: false, error: e.message }],
        }),
      )
      .finally(() => setValidating(false));
  }

  function handleApprove(): void {
    setApproving(true);
    setApproveError(null);
    filingApi
      .approve(organizationId, stateCode, period)
      .then((res) => {
        setData((prev) =>
          prev
            ? {
                ...prev,
                status: 'approved' as const,
                approved_at: res.approved_at,
                approved_by: res.approved_by,
              }
            : null,
        );
        setShowModal(false);
      })
      .catch((e: Error) => setApproveError(e.message))
      .finally(() => setApproving(false));
  }

  if (error && errorStatus === 403) {
    return (
      <div style={{ padding: 24 }}>
        <BackButton onClick={onBack} />
        <div
          style={{
            background: '#FFF1F2',
            border: '1px solid #FECDD3',
            borderRadius: 10,
            padding: '16px 20px',
          }}
        >
          <span style={{ fontSize: 13, color: '#BE123C' }}>
            You don't have permission to view this — ask your organization admin for access.
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    const isRateError = /rate|ziptax|zip\.tax/i.test(error);
    return (
      <div style={{ padding: 24 }}>
        <BackButton onClick={onBack} />
        <div
          style={{
            background: '#FFF1F2',
            border: '1px solid #FECDD3',
            borderRadius: 10,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 13, color: '#BE123C', display: 'block' }}>{error}</span>
            {isRateError && (
              <span style={{ fontSize: 12, color: '#9F1239', display: 'block', marginTop: 4 }}>
                The rate lookup service is temporarily unavailable. Tax figures may be incomplete —
                retry in a few minutes or contact support.
              </span>
            )}
          </div>
          <button
            onClick={load}
            style={{
              padding: '6px 14px',
              background: '#0F172A',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '18px 22px',
        background: '#F8FAFC',
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <BackButton onClick={onBack} />

      <div
        style={{
          display: 'flex',
          flexDirection: mobile ? 'column' : 'row',
          alignItems: mobile ? 'flex-start' : 'flex-start',
          justifyContent: 'space-between',
          gap: mobile ? 12 : 0,
          marginBottom: 16,
        }}
      >
        <div>
          {loading ? (
            <>
              <div className="skeleton-box" style={{ width: 260, height: 22, marginBottom: 8 }} />
              <div className="skeleton-box" style={{ width: 180, height: 12 }} />
            </>
          ) : (
            <>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginBottom: 2 }}>
                {stateName} — {data?.period ?? period}
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8' }}>
                {data?.period_start && data?.period_end
                  ? `${fmtDate(data.period_start)} – ${fmtDate(data.period_end)}`
                  : ''}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {!loading && data?.filing_id && (
              <button
                onClick={handleValidate}
                disabled={validating}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 18px',
                  fontSize: 13,
                  fontWeight: 700,
                  border: '1px solid #E2E8F0',
                  borderRadius: 8,
                  background: '#fff',
                  color: validating ? '#94A3B8' : '#0F172A',
                  cursor: validating ? 'default' : 'pointer',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M4.5 6.5l1.5 1.5 2.5-3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {validating ? 'Validating…' : 'Validate return'}
              </button>
            )}
            {!loading && isApproved && data?.filing_id ? (
              <DownloadButtons organizationId={organizationId} filingId={data.filing_id} />
            ) : (
              <button
                onClick={() => setShowModal(true)}
                disabled={loading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 22px',
                  fontSize: 13,
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: 8,
                  background: loading ? '#CBD5E1' : '#1B4F8C',
                  color: '#fff',
                  cursor: loading ? 'default' : 'pointer',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M2 7L5.5 10.5 11 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Approve &amp; Download
              </button>
            )}
          </div>
          {!loading && !isApproved && (
            <div style={{ fontSize: 11, color: '#94A3B8', textAlign: 'right', maxWidth: 260 }}>
              Once approved, you cannot edit this return. Corrections require filing an amendment.
            </div>
          )}
        </div>
      </div>

      <ApprovedBanner
        status={data?.status}
        approvedAt={data?.approved_at ?? undefined}
        approvedBy={data?.approved_by ?? undefined}
      />
      {validation && <ValidationPanel result={validation} onDismiss={() => setValidation(null)} />}
      <SummarySection
        data={data}
        loading={loading}
        activeFilter={txFilter}
        onFilter={setTxFilter}
      />

      {flaggedIds.size > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#FFF7ED',
            border: '1px solid #FED7AA',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2 2.5h7l-1.5 2.5 1.5 2.5H2V2.5z"
              stroke="#C2410C"
              strokeWidth="1.2"
              fill="none"
              strokeLinejoin="round"
            />
            <line
              x1="2"
              y1="10"
              x2="2"
              y2="13"
              stroke="#C2410C"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#C2410C' }}>
            {flaggedIds.size} transaction{flaggedIds.size !== 1 ? 's' : ''} flagged for amendment.
          </span>
          <span style={{ fontSize: 12, color: '#92400E' }}>
            Our team will review and process the correction.
          </span>
        </div>
      )}

      {txFilter && (
        <TransactionPanel
          organizationId={organizationId}
          stateCode={stateCode}
          period={period}
          filter={txFilter}
          onClose={() => setTxFilter(null)}
          flaggedIds={flaggedIds}
          onFlag={(tx) => setFlagTarget(tx)}
        />
      )}

      <JurisdictionTable jurisdictions={data?.jurisdictions} loading={loading} />

      {showModal && (
        <ApproveModal
          data={data}
          stateName={stateName}
          period={data?.period ?? period}
          onConfirm={handleApprove}
          onCancel={() => {
            setShowModal(false);
            setApproveError(null);
          }}
          approving={approving}
          approveError={approveError}
        />
      )}

      {flagTarget && (
        <FlagModal
          tx={flagTarget}
          organizationId={organizationId}
          onClose={() => setFlagTarget(null)}
          onFlagged={(txId) => setFlaggedIds((prev) => new Set([...prev, txId]))}
        />
      )}
    </div>
  );
}
